import React, { Suspense, lazy } from "react";
import "./Manipulatives.css";
import type { ManipulativeKind, ManipulativeProps, ManipulativeSpec } from "./kinds.ts";
import { useTranslation } from "../Shared/i18n/useTranslation.ts";
import type { StringKey } from "../Shared/i18n/strings.ts";

/**
 * The panel a worksheet page carries when its manifest entry has a `manipulative` (ADR 0117): a
 * heading, one paragraph about the tool, and the tool itself in a stage whose height is reserved per
 * kind in Manipulatives.css. scripts/prerender.js writes the same heading, paragraph and an empty
 * stage; change both.
 *
 * Each tool is its own lazy chunk, so a page with no manipulative downloads none of this code and a
 * clock page downloads only the clock. A new kind adds a folder, an entry here and its strings.
 */
const TOOLS: Record<ManipulativeKind, React.LazyExoticComponent<React.FC<ManipulativeProps>>> = {
    baseTen: lazy(() => import("./BaseTen/BaseTenBlocks.tsx")),
    fractionBars: lazy(() => import("./FractionBars/FractionBars.tsx")),
    clock: lazy(() => import("./Clock/GearedClock.tsx")),
    numberLine: lazy(() => import("./NumberLine/NumberLine.tsx")),
    doubleNumberLine: lazy(() => import("./DoubleNumberLine/DoubleNumberLine.tsx")),
};

/**
 * The panel's words, one literal `t()` call per key: StringKeys.test.js finds keys by scanning for
 * them, so a key built from the kind's name would look unused.
 */
const COPY: Record<ManipulativeKind, (t: (key: StringKey) => string) => { heading: string; description: string }> = {
    baseTen: (t) => ({
        heading: t("manipulatives.baseTenHeading"),
        description: t("manipulatives.baseTenDescription"),
    }),
    fractionBars: (t) => ({
        heading: t("manipulatives.fractionBarsHeading"),
        description: t("manipulatives.fractionBarsDescription"),
    }),
    clock: (t) => ({
        heading: t("manipulatives.clockHeading"),
        description: t("manipulatives.clockDescription"),
    }),
    numberLine: (t) => ({
        heading: t("manipulatives.numberLineHeading"),
        description: t("manipulatives.numberLineDescription"),
    }),
    doubleNumberLine: (t) => ({
        heading: t("manipulatives.doubleNumberLineHeading"),
        description: t("manipulatives.doubleNumberLineDescription"),
    }),
};

export function ManipulativePanel({ spec }: { spec: ManipulativeSpec }) {
    const t = useTranslation();
    const Tool = TOOLS[spec.kind];
    const { heading, description } = COPY[spec.kind](t);
    const headingId = `manipulative-heading-${spec.kind}`;

    return (
        <section className="landingManipulative" aria-labelledby={headingId}>
            <h2 id={headingId}>{heading}</h2>
            <p>{description}</p>
            <div className="manipStage" data-kind={spec.kind} data-preset={spec.preset}>
                <Suspense fallback={null}>
                    <Tool preset={spec.preset} />
                </Suspense>
            </div>
        </section>
    );
}

export default ManipulativePanel;
